import { LOCAL_STORAGE_EMAIL_SEARCH_CONDITIONS_KEY } from "constants/local-storage-keys";
import type { EmailSearchInput } from "hooks/useEmailsRestApi";

/**
 * メール検索条件をlocalStorageに保存・復元するUtilityクラス
 */
export class SearchConditionsStorage {
  /**
   * 検索条件をlocalStorageに保存する
   * @param {EmailSearchInput} conditions - 検索条件
   * @param {boolean} isMatter - 案件/人材の選択状態
   */
  static saveSearchConditions(
    conditions: EmailSearchInput,
    isMatter: boolean,
  ): void {
    try {
      // 既存データからviewModeを維持しつつ上書き
      const existing = SearchConditionsStorage.loadSearchConditions();
      const searchData = {
        conditions,
        isMatter,
        viewMode: existing.viewMode,
        timestamp: Date.now(),
      };
      localStorage.setItem(
        LOCAL_STORAGE_EMAIL_SEARCH_CONDITIONS_KEY,
        JSON.stringify(searchData),
      );
    } catch (_error) {
      // エラーを無視
    }
  }

  /**
   * 表示形式をlocalStorageに保存する
   * @param {string} viewMode - 表示形式 ("card" | "table")
   */
  static saveViewMode(viewMode: "card" | "table"): void {
    try {
      const saved = localStorage.getItem(
        LOCAL_STORAGE_EMAIL_SEARCH_CONDITIONS_KEY,
      );
      const searchData = saved ? JSON.parse(saved) : { timestamp: Date.now() };
      searchData.viewMode = viewMode;
      localStorage.setItem(
        LOCAL_STORAGE_EMAIL_SEARCH_CONDITIONS_KEY,
        JSON.stringify(searchData),
      );
    } catch (_error) {
      // エラーを無視
    }
  }

  /**
   * 保存された検索条件をlocalStorageから読み込む
   * @return {Object} 検索条件とisMatterとviewModeの状態。保存されていない場合はnull
   * @return {EmailSearchInput | null} return.conditions - 検索条件
   * @return {boolean | null} return.isMatter - 案件/人材の選択状態
   * @return {string | null} return.viewMode - 表示形式
   */
  static loadSearchConditions(): {
    conditions: EmailSearchInput | null;
    isMatter: boolean | null;
    viewMode: "card" | "table" | null;
  } {
    try {
      const saved = localStorage.getItem(
        LOCAL_STORAGE_EMAIL_SEARCH_CONDITIONS_KEY,
      );
      if (!saved) {
        return { conditions: null, isMatter: null, viewMode: null };
      }

      const searchData = JSON.parse(saved);

      // 24時間以内のデータのみ有効とする
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (Date.now() - searchData.timestamp > twentyFourHours) {
        SearchConditionsStorage.clearSearchConditions();
        return { conditions: null, isMatter: null, viewMode: null };
      }

      return {
        conditions: searchData.conditions,
        isMatter: searchData.isMatter,
        viewMode: searchData.viewMode === "card" ? "card" : null,
      };
    } catch (_error) {
      return { conditions: null, isMatter: null, viewMode: null };
    }
  }

  /**
   * 保存された検索条件をlocalStorageから削除する
   */
  static clearSearchConditions(): void {
    try {
      localStorage.removeItem(LOCAL_STORAGE_EMAIL_SEARCH_CONDITIONS_KEY);
    } catch (_error) {
      // エラーを無視
    }
  }
}
